import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { AuthButton } from '@/features/auth/components/auth-button';
import { AuthForm } from '@/features/auth/components/auth-form';
import { AuthLayout } from '@/features/auth/components/auth-layout';
import { AuthLink } from '@/features/auth/components/auth-link';

const registerFormSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .regex(/^\+?[\d\s\-()]+$/, 'Please enter a valid phone number'),
  email: z.email('Please enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one lowercase letter, one uppercase letter, and one number',
    ),
});

type RegisterFormData = z.infer<typeof registerFormSchema>;

function RegisterPage() {
  const [isLoading, setIsLoading] = useState(false);

  const { auth } = Route.useRouteContext();
  const { redirectTo } = Route.useSearch();
  const { signUp, signIn } = auth;
  const navigate = useNavigate();

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      password: '',
    },
  });

  const handleSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    form.clearErrors('root');

    try {
      // First, create the account with metadata
      const signUpResult = await signUp({
        email: data.email,
        password: data.password,
        phone: data.phoneNumber,
        name: data.fullName,
      });

      if (signUpResult.error) {
        form.setError('root', {
          type: 'manual',
          message: signUpResult.error.message || 'Failed to create account',
        });
        return;
      }

      // Then sign in automatically
      const signInResult = await signIn({
        email: data.email,
        password: data.password,
      });

      if (signInResult.error) {
        form.setError('root', {
          type: 'manual',
          message:
            'Account created but failed to sign in. Please try signing in manually.',
        });
        return;
      }

      // Success - redirect
      await navigate({ to: redirectTo });
    } catch {
      form.setError('root', {
        type: 'manual',
        message: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthForm
        onSubmit={form.handleSubmit(handleSubmit)}
        title="Sign up for your account"
      >
        <Form {...form}>
          <div className="grid grid-rows-[auto_1fr] gap-4">
            {/* Error message area - only takes space when needed */}
            <div className="overflow-hidden">
              <div
                className={`transition-all duration-200 ${
                  form.formState.errors.root
                    ? 'max-h-20 opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {form.formState.errors.root?.message}
                  </div>
                </div>
              </div>
            </div>

            {/* Form fields area */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="John Doe"
                        autoComplete="name"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        autoComplete="tel"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="name@example.com"
                        autoComplete="email"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder="Password"
                        autoComplete="new-password"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Form>

        <AuthButton type="submit" disabled={isLoading}>
          {isLoading ? 'Signing up...' : 'Sign up'}
        </AuthButton>
      </AuthForm>
      <AuthLink to="/login" text="Have an account?" linkText="Sign in" />
    </AuthLayout>
  );
}

const registerSearchSchema = z.object({
  redirectTo: z.string().catch('/'),
});

export const Route = createFileRoute('/register')({
  validateSearch: registerSearchSchema,
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirectTo });
    }
  },
  component: RegisterPage,
});
